import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, BookOpen, Plus, Edit, Trash2,
  AlertCircle, CheckCircle, XCircle, Search, Shield
} from "lucide-react";
import axios from "axios";
import { getApiUrl } from "../../config/api";
import "../../styles/UtopiaPages.css";

interface GlobalSubject {
  id: number;
  name: string;
  short_name?: string | null;
  tenant_id: number | null;
  createdAt?: string | null;
}

const GlobalSubjects: React.FC = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token") || "";

  const [subjects, setSubjects] = useState<GlobalSubject[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>("");
  const [success, setSuccess] = useState<string>("");

  // Form states
  const [showModal, setShowModal] = useState(false);
  const [editingSubject, setEditingSubject] = useState<GlobalSubject | null>(null);
  const [subjectName, setSubjectName] = useState("");
  const [subjectShortName, setSubjectShortName] = useState("");
  const [formLoading, setFormLoading] = useState(false);

  // Search
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    fetchSubjects();
  }, []);

  const fetchSubjects = async () => {
    try {
      setLoading(true);
      const response = await axios.get(getApiUrl("superadmin/subjects"), {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubjects(response.data);
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to fetch subjects");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOrUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!subjectName.trim()) return;

    setFormLoading(true);
    try {
      if (editingSubject) {
        await axios.put(
          getApiUrl(`superadmin/subjects/${editingSubject.id}`),
          { name: subjectName, short_name: subjectShortName },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSuccess("Subject updated successfully");
      } else {
        await axios.post(
          getApiUrl("superadmin/subjects"),
          { name: subjectName, short_name: subjectShortName },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSuccess("Subject created successfully");
      }
      setShowModal(false);
      setSubjectName("");
      setSubjectShortName("");
      setEditingSubject(null);
      fetchSubjects();
    } catch (err: any) {
      if (err.response?.status === 409) {
        const existingName = err.response?.data?.existingSubject?.name || subjectName.trim();
        setError(`${err.response?.data?.message || "This subject already exists"}. It has been highlighted in the catalog below.`);
        setSearchQuery(existingName);
        setShowModal(false);
        setEditingSubject(null);
        setSubjectName("");
        setSubjectShortName("");
        await fetchSubjects();
      } else {
        setError(err.response?.data?.message || "Failed to save subject");
      }
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (id: number, name: string) => {
    if (!window.confirm(`Are you sure you want to delete "${name}"?`)) {
      return;
    }

    try {
      await axios.delete(getApiUrl(`superadmin/subjects/${id}`), {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess("Subject deleted successfully");
      fetchSubjects();
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to delete subject");
    }
  };

  const openEditModal = (subject: GlobalSubject) => {
    setEditingSubject(subject);
    setSubjectName(subject.name);
    setSubjectShortName(subject.short_name || "");
    setShowModal(true);
  };

  const filteredSubjects = subjects.filter(s =>
    s.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatCreatedDate = (value?: string | null) => {
    if (!value) return "-";
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime())
      ? "-"
      : parsed.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "long",
          year: "numeric"
        });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-vh-100 bg-white">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-teal-500/20 border-t-teal-500 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-400 font-black text-[10px] uppercase tracking-[0.2em]">Loading Subjects...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <main className="dashboard-main p-8 bg-slate-50/30">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-10">
          <div className="flex items-center gap-6">
            <button
              onClick={() => navigate("/superadmin")}
              className="w-12 h-12 flex items-center justify-center bg-white border border-slate-100 rounded-2xl text-slate-400 hover:text-teal-600 hover:shadow-xl hover:shadow-teal-500/5 transition-all"
            >
              <ArrowLeft size={20} />
            </button>
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-xl bg-indigo-500 flex items-center justify-center shadow-lg shadow-indigo-500/20">
                  <BookOpen size={20} className="text-white" />
                </div>
                <span className="text-[10px] font-black text-indigo-600 uppercase tracking-[0.2em] bg-indigo-50 px-3 py-1 rounded-full">Global Templates</span>
              </div>
              <h1 className="text-3xl font-black text-slate-800 tracking-tight">Global Subjects</h1>
              <p className="text-slate-400 mt-1 font-medium">Manage the shared subject catalog used by every school</p>
              <p className="text-xs font-semibold text-slate-500 mt-2 uppercase tracking-[0.16em]">
                {subjects.length} shared subject{subjects.length === 1 ? "" : "s"} in catalog
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="hidden md:flex items-center gap-2 rounded-2xl border border-indigo-200 bg-indigo-50 px-4 py-3 text-[10px] font-black uppercase tracking-[0.16em] text-indigo-700">
              <Shield size={14} />
              Shared With All Schools
            </div>
            <button
              onClick={() => {
                setEditingSubject(null);
                setSubjectName("");
                setSubjectShortName("");
                setShowModal(true);
              }}
              className="flex items-center gap-2 bg-indigo-500 text-white px-6 py-3 rounded-2xl font-black text-[10px] uppercase tracking-widest hover:bg-indigo-600 transition-all shadow-xl shadow-indigo-500/20"
            >
              <Plus size={16} /> Add Subject
            </button>
          </div>
        </div>

        {/* Alerts */}
        {error && (
          <div className="bg-rose-50 border border-rose-200 p-4 rounded-xl flex items-center gap-3 mb-6">
            <AlertCircle className="text-rose-500 shrink-0" size={20} />
            <p className="text-rose-700 text-sm">{error}</p>
            <button onClick={() => setError("")} className="ml-auto text-rose-500 hover:text-rose-700">
              <XCircle size={18} />
            </button>
          </div>
        )}

        {success && (
          <div className="bg-teal-50 border border-teal-200 p-4 rounded-xl flex items-center gap-3 mb-6">
            <CheckCircle className="text-teal-500 shrink-0" size={20} />
            <p className="text-teal-700 text-sm">{success}</p>
            <button onClick={() => setSuccess("")} className="ml-auto text-teal-500 hover:text-teal-700">
              <XCircle size={18} />
            </button>
          </div>
        )}

        {/* Search */}
        <div className="bg-white rounded-[2rem] border border-slate-100 shadow-xl shadow-slate-200/50 p-6 mb-8">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
            <input
              type="text"
              placeholder="Search subjects..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-4 rounded-xl border border-slate-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none transition-all"
            />
          </div>
        </div>

        {/* Subjects List */}
        <div className="bg-white rounded-[2.5rem] border border-slate-100 shadow-2xl shadow-slate-200/30 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50/50 border-b border-slate-100">
                <th className="px-8 py-6 text-left text-[10px] font-black text-slate-400 uppercase tracking-widest">Subject Name</th>
                <th className="px-8 py-6 text-left text-[10px] font-black text-slate-400 uppercase tracking-widest">Created</th>
                <th className="px-8 py-6 text-right text-[10px] font-black text-slate-400 uppercase tracking-widest">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredSubjects.length > 0 ? (
                filteredSubjects.map((subject) => (
                  <tr key={subject.id} className="group hover:bg-slate-50/50 transition-all border-b border-slate-50">
                    <td className="px-8 py-6">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center">
                          <BookOpen size={18} className="text-indigo-600" />
                        </div>
                        <span className="font-bold text-slate-800">{subject.name}</span>
                      </div>
                    </td>
                    <td className="px-8 py-6">
                      <span className="text-sm text-slate-500">
                        {formatCreatedDate(subject.createdAt)}
                      </span>
                    </td>
                    <td className="px-8 py-6">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => openEditModal(subject)}
                          className="p-2.5 rounded-xl bg-slate-50 text-slate-400 hover:bg-teal-50 hover:text-teal-600 transition-all"
                          title="Edit"
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          onClick={() => handleDelete(subject.id, subject.name)}
                          className="p-2.5 rounded-xl bg-slate-50 text-slate-400 hover:bg-rose-50 hover:text-rose-600 transition-all"
                          title="Delete"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={3} className="px-8 py-16 text-center">
                    <BookOpen size={48} className="mx-auto mb-4 text-slate-200" />
                    <p className="text-slate-400 font-medium">No subjects found</p>
                    <button
                      onClick={() => {
                        setEditingSubject(null);
                        setSubjectName("");
                        setSubjectShortName("");
                        setShowModal(true);
                      }}
                      className="mt-4 text-indigo-500 font-bold hover:underline"
                    >
                      Create your first subject
                    </button>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </main>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-[2rem] p-8 w-full max-w-md shadow-2xl">
            <h2 className="text-xl font-black text-slate-800 mb-6">
              {editingSubject ? "Edit Subject" : "Create New Subject"}
            </h2>
            <form onSubmit={handleCreateOrUpdate}>
              <div className="mb-6">
                <label className="block text-sm font-bold text-slate-600 mb-2 uppercase tracking-wide">
                  Subject Name
                </label>
                <input
                  type="text"
                  value={subjectName}
                  onChange={(e) => setSubjectName(e.target.value)}
                  placeholder="e.g., Creative and Technology Studies"
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none transition-all"
                  autoFocus
                />
              </div>
              <div className="mb-6">
                <label className="block text-sm font-bold text-slate-600 mb-2 uppercase tracking-wide">
                  Short Name
                </label>
                <input
                  type="text"
                  value={subjectShortName}
                  onChange={(e) => setSubjectShortName(e.target.value)}
                  placeholder="e.g., C.T.S"
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none transition-all"
                />
              </div>
              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setSubjectName("");
                    setSubjectShortName("");
                    setEditingSubject(null);
                  }}
                  className="flex-1 px-6 py-3 border border-slate-200 rounded-xl text-slate-600 font-bold hover:bg-slate-50 transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={formLoading || !subjectName.trim()}
                  className="flex-1 px-6 py-3 bg-indigo-500 text-white rounded-xl font-bold hover:bg-indigo-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {formLoading ? "Saving..." : editingSubject ? "Update" : "Create"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default GlobalSubjects;
